/* WHIFF Product World interactions */
(() => {
  const state = { initialized: new WeakSet(), cleanup: [] };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveals(root = document) {
    const nodes = [...root.querySelectorAll('.whiff-world-reveal:not(.is-visible)')];
    if (!nodes.length) return;
    if (reduced() || !('IntersectionObserver' in window)) {
      nodes.forEach(n => n.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -5% 0px' });
    nodes.forEach(n => observer.observe(n));
  }

  function initCycles(root = document) {
    root.querySelectorAll('[data-whiff-cycle]').forEach((cycle) => {
      if (state.initialized.has(cycle)) return;
      state.initialized.add(cycle);
      const slides = [...cycle.querySelectorAll('[data-whiff-cycle-slide]')];
      const dots = [...cycle.querySelectorAll('[data-whiff-cycle-dot]')];
      if (slides.length < 2 || reduced()) {
        slides[0]?.classList.add('is-active');
        dots[0]?.classList.add('is-active');
        return;
      }
      const interval = Math.max(1200, Number(cycle.dataset.interval || 4000));
      let index = 0;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      };
      const start = () => {
        clearInterval(timer);
        if (cycle.dataset.autoplay === 'true') timer = setInterval(() => show(index + 1), interval);
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      if (cycle.dataset.pauseHover === 'true') {
        cycle.addEventListener('mouseenter', () => clearInterval(timer));
        cycle.addEventListener('mouseleave', start);
      }
      show(0); start();
    });
  }


  function initNotes(root = document) {
    root.querySelectorAll('[data-whiff-notes]').forEach((section) => {
      if (state.initialized.has(section)) return;
      state.initialized.add(section);
      const notes = [...section.querySelectorAll('[data-whiff-note]')];
      if (!notes.length) return;
      const activate = (target) => {
        notes.forEach((note) => note.classList.toggle('is-active', note === target));
      };
      notes.forEach((note) => {
        note.addEventListener('focus', () => activate(note));
        note.addEventListener('click', () => activate(note));
        note.addEventListener('pointerenter', () => {
          if (window.matchMedia('(hover:hover)').matches) activate(note);
        });
      });
      section.addEventListener('pointerleave', () => {
        if (window.matchMedia('(hover:hover)').matches) notes.forEach((note) => note.classList.remove('is-active'));
      });
    });
  }

  function initCountdowns(root = document) {
    root.querySelectorAll('[data-whiff-countdown]').forEach((el) => {
      if (state.initialized.has(el)) return;
      state.initialized.add(el);
      const raw = el.dataset.whiffCountdown;
      if (!raw) return;
      const target = new Date(raw).getTime();
      if (!Number.isFinite(target)) { el.textContent = raw; return; }
      const render = () => {
        const diff = Math.max(0, target - Date.now());
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        el.textContent = `${String(d).padStart(2,'0')}:${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      };
      render(); const timer = setInterval(render, 1000); state.cleanup.push(() => clearInterval(timer));
    });
  }

  function initParallax() {
    if (reduced()) return;
    const items = [...document.querySelectorAll('[data-parallax]')];
    if (!items.length) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const strength = Number(el.dataset.parallax || 0);
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        el.style.setProperty('--ww-parallax-y', `${progress * strength * -1}px`);
        const base = getComputedStyle(el).transform;
        el.style.translate = `0 var(--ww-parallax-y)`;
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initSticky(root = document) {
    root.querySelectorAll('[data-whiff-sticky]').forEach((bar) => {
      if (state.initialized.has(bar)) return;
      state.initialized.add(bar);
      const hero = document.querySelector('[data-whiff-hero]');
      const purchase = document.querySelector('[data-whiff-purchase]');
      const afterPx = Number(bar.dataset.afterPx || 0);
      const hideAtPurchase = bar.dataset.hideAtPurchase !== 'false';
      let raf = 0;
      const update = () => {
        raf = 0;
        if (!hero) { bar.classList.add('is-visible'); return; }
        const heroRect = hero.getBoundingClientRect();
        const passedHero = heroRect.bottom <= afterPx;
        let purchaseVisible = false;
        if (purchase && hideAtPurchase) {
          const p = purchase.getBoundingClientRect();
          purchaseVisible = p.top < window.innerHeight * .92 && p.bottom > window.innerHeight * .08;
        }
        bar.classList.toggle('is-visible', passedHero && !purchaseVisible);
      };
      const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    });
  }


  function initBackToTop(root = document) {
    root.querySelectorAll('[data-whiff-backtop]').forEach((button) => {
      if (state.initialized.has(button)) return;
      state.initialized.add(button);
      const update = () => button.classList.toggle('is-visible', window.scrollY > window.innerHeight * .45);
      button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' }));
      window.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  function initGlobalHeaderMode(root = document) {
    const hero = root.querySelector?.('[data-whiff-hero]') || document.querySelector('[data-whiff-hero]');
    if (!hero) return;
    document.body.classList.toggle('whiff-world-hide-global-header', hero.dataset.hideGlobalHeader === 'true');
  }

  function initVariantSync(root = document) {
    root.querySelectorAll('[data-whiff-variant-select]').forEach((select) => {
      if (state.initialized.has(select)) return;
      state.initialized.add(select);
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const id = select.value;
        document.querySelectorAll('[data-whiff-variant-id]').forEach((input) => { input.value = id; });
        document.querySelectorAll('[data-whiff-price-live]').forEach((node) => {
          if (option?.dataset.price) node.textContent = option.dataset.price;
        });
        document.querySelectorAll('[data-whiff-sticky-price]').forEach((node) => {
          if (option?.dataset.price) node.textContent = option.dataset.price;
        });
      });
    });
  }

  function init(root = document) {
    initGlobalHeaderMode(root);
    initReveals(root);
    initCycles(root);
    initNotes(root);
    initCountdowns(root);
    initVariantSync(root);
    initSticky(root);
    initBackToTop(root);
    initParallax();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init(document));
  document.addEventListener('shopify:block:select', (event) => {
    event.target?.classList?.add('is-visible');
  });
})();
